import { useEffect } from "react";
import { ThemeEnum } from "./types";

export default function useSystemThemeListener({
    theme,
    applyTheme,
    forceSystemTheme,
}: {
    theme: ThemeEnum | null;
    applyTheme: (mode: ThemeEnum) => void;
    forceSystemTheme?: boolean;
}): void {

    useEffect(() => {
        const mql = window.matchMedia("(prefers-color-scheme: dark)");

        const systemThemeChangeListener = (e: MediaQueryListEvent) => {
            // There are two possible behaviors for system theme changes, defined by forceSystemTheme:
            // 1. forceSystemTheme is false:
            //    The app is set to "System", so it adopts the system theme and only updates the HTML class.
            //    The theme change is perceived visually, but the app configuration remains "System".
            //    Note: This only works when the theme is set to "System". If it's set to "Light" or "Dark",
            //    system changes won't affect the app.

            // 2. forceSystemTheme is true:
            //    The app may be set to Light, Dark, or System, but it doesn't matter.
            //    A system theme change will override the app theme, updating HTML, state, and localStorage.
            //    In other words, the system theme forcibly controls the app theme.
            
            const systemPreferredTheme = e.matches ? ThemeEnum.DARK : ThemeEnum.LIGHT;

            if (forceSystemTheme) {
                // ⚡️ Case 2: override everything from system
                applyTheme(systemPreferredTheme);
                localStorage.setItem("theme", systemPreferredTheme);
            } else if (theme === ThemeEnum.SYSTEM) {
                // 🌘 Case 1: only update HTML, keep theme as "System"
                document.documentElement.classList.toggle(ThemeEnum.DARK, e.matches);
            }
        };

        mql.addEventListener("change", systemThemeChangeListener);
        return () => {
            mql.removeEventListener("change", systemThemeChangeListener);
        };
    }, [theme, forceSystemTheme]);
}